/* eslint-disable */
import React, { useState } from 'react';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { conformToMask } from 'react-text-mask';
import { useMutation } from 'react-fetching-library';
import { compare } from 'fast-json-patch';
import FormikTextField from 'components/FormikFields/FormikTextField';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import { FormattedPhone, MASK } from 'components/Shared/FormattedPhone';
import getDigitPhone from 'helpers/phone';
import showServerError from 'helpers/showError';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../redux_store/reducer/reducers/notificationsReducer';
import { setUser } from '../../redux_store/reducer/reducers/userReducer/userReducer';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(10),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
  },
  section: {
    width: '100%',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#031F42',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors?.lightBlue || '#00A8FF'}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1),
    },
  },
  sectionContent: {
    marginTop: theme.spacing(2),
    maxWidth: 1000,
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const ProfileEdit = () => {
  const classes = useStyles();
  const { en: { titles, errors, notifications, forms, btn } } = useSelector((state) => state.i18n);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [backendErrors, setBackendErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const profileEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: '/user',
    body: patches,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    profileEditAction,
  ]);

  const initialValues = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
  };

  const editProfile = async (values, { setSubmitting }) => {
    // remove non-digits from phone
    const formattedValues = { ...values, phone: getDigitPhone(values.phone) };
    const patches = compare(initialValues, formattedValues);

    if (patches.length) {
      setBackendErrors({});
      const { payload, error, status } = await mutate(patches);
      if (error) {
        setIsFailed(true);
        setTimeout(() => {
          setIsFailed(false);
        }, 2000);
        const options = payload && {
          correlationUUID: payload.correlationUUID,
          userUUID: user.uuid,
        };
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.validationError);
            setBackendErrors(payload.validationErrors);
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      } else if (payload) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
        dispatch(setUser(payload));
        dispatch(
          enqueueSnackbar({
            message: notifications.profileUpdated,
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }),
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        phone: conformToMask(user.phone, MASK).conformedValue, // phone number need to be formatted
      }}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={editProfile}
    >
      {({ isValid, isSubmitting, dirty, handleSubmit }) => (
        <ContentWrapper
          titleText={titles.editProfile}
          containerStyle={{
            overflow: 'visible',
            maxHeight: 'calc(100% - 35px)',
            boxSizing: 'border-box',
            paddingBottom: 0,
          }}
        >
          <div className={classes.contentContainer}>
            <div className={classes.formCard}>
              <div className={classes.scrollableContent}>
                <div className={classes.formContent}>
                  <Form>
                    {/* Personal Information Section */}
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        {forms.personalInformation || 'Personal Information'}
                      </Typography>
                      <div className={classes.sectionContent}>
                        <Grid container direction='column'>
                          <FormikTextField
                            type='text'
                            name='firstName'
                            label={forms.firstName}
                            backendError={backendErrors.firstName}
                            required
                          />
                          <FormikTextField
                            type='text'
                            name='lastName'
                            label={forms.lastName}
                            backendError={backendErrors.lastName}
                            required
                          />
                        </Grid>
                      </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        {forms.contactInformation || 'Contact Information'}
                      </Typography>
                      <div className={classes.sectionContent}>
                        <Grid container direction='column'>
                          <FormikTextField
                            type='email'
                            name='email'
                            label={forms.email}
                            autoComplete='email'
                            backendError={backendErrors.email}
                            required
                          />
                          <FormikTextField
                            type='tel'
                            name='phone'
                            label={forms.phone}
                            backendError={backendErrors.phone}
                            InputProps={{
                              inputComponent: FormattedPhone,
                            }}
                            required
                          />
                        </Grid>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || isSubmitting || !dirty}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                onClick={handleSubmit}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>
        </ContentWrapper>
      )}
    </Formik>
  );
};

export default ProfileEdit;
